import React, { useEffect, useRef, useState } from 'react';

const STYLE_MAP = {
  'Premium dark': { bg: 'linear-gradient(135deg, #2a1d4f, #6E5BFF)', text: '#fff', accent: '#E4B43F' },
  'Clean white': { bg: 'linear-gradient(135deg, #fafaf5, #ede9dc)', text: '#15161A', accent: '#C49628' },
  'Minimal': { bg: '#1a1d24', text: '#F5F7FA', accent: '#9CA3AF' },
  'Bold viral': { bg: 'linear-gradient(135deg, #FF6B35, #F7B731)', text: '#fff', accent: '#fff' },
  'AI gradient': { bg: 'linear-gradient(135deg, #6E5BFF, #E4B43F)', text: '#fff', accent: '#fff' },
};

export const SlideSwiper = ({ slides, brand, style = 'Premium dark', activeIndex, onActiveChange, onOpenImage }) => {
  const [internal, setInternal] = useState(0);
  const containerRef = useRef(null);
  const startXRef = useRef(null);
  const isControlled = typeof activeIndex === 'number';
  const current = isControlled ? activeIndex : internal;
  const setActive = (next) => {
    const clamped = Math.max(0, Math.min(slides.length - 1, next));
    if (isControlled) onActiveChange?.(clamped);
    else setInternal(clamped);
  };

  useEffect(() => {
    if (current > slides.length - 1) setActive(0);
  }, [slides.length]);

  const slide = slides[current] || { headline: '', sub: '' };
  const hasImage = Boolean(slide.imageUrl);
  const sty = STYLE_MAP[style] || STYLE_MAP['Premium dark'];

  const onTouchStart = (event) => {
    startXRef.current = event.touches[0].clientX;
  };
  const onTouchEnd = (event) => {
    if (startXRef.current == null) return;
    const delta = event.changedTouches[0].clientX - startXRef.current;
    if (Math.abs(delta) > 36) setActive(current + (delta < 0 ? 1 : -1));
    startXRef.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={'ig-slides ' + (hasImage ? 'has-image' : '')}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={'ig-slide ' + (hasImage ? 'has-image' : '')} style={{ background: sty.bg, color: sty.text }}>
        {hasImage && (
          <button
            type="button"
            className="ig-slide-image-button"
            onClick={() => onOpenImage?.({ src: slide.imageUrl, alt: slide.headline || `Slide ${current + 1}`, label: slide.headline || slide.label || `Slide ${current + 1}` })}
            aria-label="Open full image preview"
          >
            <img className="ig-slide-image" src={slide.imageUrl} alt={slide.headline || `Slide ${current + 1}`} loading="lazy" />
          </button>
        )}
        {!hasImage && (
          <>
            <div className="ig-slide-num" style={{ borderColor: sty.accent + '50', color: sty.text }}>
              {current + 1}/{slides.length}
            </div>
            <div className="ig-headline" style={{ color: sty.text }}>
              {String(slide.headline || '').split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            {slide.sub && (
              <div className="ig-sub" style={{ color: sty.text, opacity: 0.75 }}>{slide.sub}</div>
            )}
            {brand && <div className="ig-brand" style={{ color: sty.accent }}>{brand}</div>}
          </>
        )}
      </div>
      <div className="ig-dots">
        {slides.map((_, i) => (
          <span
            key={i}
            onClick={() => setActive(i)}
            className={i === current ? 'on' : ''}
            role="button"
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default SlideSwiper;
